import { useCallback, useEffect } from 'react';
import {
  Flex,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import type { FlipDescriptionCardProps, FlipDescriptionCardEntity } from './types';
import { bridge } from '../../../../utils';
import { localizeNumber } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

export const FlipDescriptionCard = ({
  entity,
  logging,
}: FlipDescriptionCardProps) => {
  if (!entity || entity.length === 0) return null;

  return (
    <Flex
      margin={{ horizontal: 16 }}
      backgroundColor="bg_secondary"
      borderRadius={8}
      padding={{ horizontal: 12, vertical: 8 }}
      gap={8}
    >
      {entity.slice(0, 3).map(item => (
        <FlipCard
          key={item.item.sno}
          entity={item}
          logging={logging}
        />
      ))}
    </Flex>
  );
};

const FlipCard = ({
  entity,
  logging,
}: {
  entity: FlipDescriptionCardEntity;
  logging: IScreensLogging;
}) => {
  const { item } = entity;
  const { openDeeplink } = useOpenDeeplink();

  useEffect(() => {
    bridge.logAnalyticsEvent({
      name: 'DISPLAY_DESCRIPTION_CARD',
      analytics: { ...logging?.analytics, ...entity.logging?.analytics },
      ably_only_log: { ...logging?.ably_only_log, ...entity.logging?.ably_only_log },
    });
  }, [logging, entity.logging]);

  const handlePress = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_DESCRIPTION_CARD',
      analytics: { ...logging?.analytics, ...entity.logging?.analytics },
      ably_only_log: { ...logging?.ably_only_log, ...entity.logging?.ably_only_log },
    });
    openDeeplink(item.deeplink);
  }, [item.deeplink, logging, entity.logging, openDeeplink]);

  return (
    <Flex.Row
      backgroundColor="white"
      borderRadius={4}
      overflow="hidden"
      onPress={handlePress}
    >
      {/* 좌측 이미지 */}
      <Flex width={70} height={83}>
        <RemoteImage
          uri={item.image}
          width={70}
          height={83}
          resizeMode="cover"
        />
      </Flex>

      {/* 우측 텍스트 */}
      <Flex flex={1} padding={{ horizontal: 8, vertical: 8 }} justifyContent="center" gap={2}>
        {item.badge_description.length > 0 && (
          <Flex alignSelf="flex-start">
            <Flex
              backgroundColor="bg_lilac_primary"
              borderRadius={2}
              padding={{ horizontal: 4, vertical: 2 }}
            >
              <Typography type="body5" color="content_always_white">
                {item.badge_description}
              </Typography>
            </Flex>
          </Flex>
        )}
        <Typography type="body4" color="content_primary" numberOfLines={1}>
          {item.name}
        </Typography>
        <Flex.Row alignItems="center" gap={4}>
          {item.discount_rate > 0 && (
            <Typography type="subtitle2" color="content_pink_primary">
              {item.discount_rate}%
            </Typography>
          )}
          <Typography type="subtitle2" color="content_primary">
            {localizeNumber(item.price)}원
          </Typography>
        </Flex.Row>
        {item.sell_count > 0 && (
          <Typography type="body5" color="content_secondary">
            {localizeNumber(item.sell_count)}명 구매 중
          </Typography>
        )}
      </Flex>
    </Flex.Row>
  );
};
